import { supabase } from '../../lib/supabase';
import { slugify } from '../market-intelligence/utils';
import { fetchMandiListings } from '../mandi/data';
import { normalizeFactoryName, normalizeGrade } from './matching';

// Builds a normalizedFactoryName -> { factorySlug, grades: Map(normalizedGrade -> mandiGrade) }
// lookup from the SAME live Mandi data the /mandi page itself renders — never a
// separate/duplicated query, so a match here always reflects a real, currently
// live, currently in-stock Mandi offer.
const buildMandiIndex = (mandiFactories) => {
  const index = new Map();
  mandiFactories.forEach((f) => {
    const key = normalizeFactoryName(f.name);
    const grades = new Map();
    f.grades.forEach((g) => grades.set(normalizeGrade(g.grade), g));
    index.set(key, { factorySlug: slugify(f.name), factoryName: f.name, grades });
  });
  return index;
};

// The single most-recent tender_date_time in the whole table — the reference
// point ("today") that the initial 7-day window and the monthly Load More
// limit are both anchored to. Deliberately NOT wall-clock time: the page
// should still show its latest available week even if the crawl is stale.
export const fetchLatestTenderDate = async () => {
  const { data, error } = await supabase
    .from('ebuysugar_tender_listings')
    .select('tender_date_time')
    .order('tender_date_time', { ascending: false })
    .limit(1);
  if (error) return { latestTenderDate: null, error: error.message };
  return { latestTenderDate: data?.[0]?.tender_date_time ?? null, error: null };
};

// collected_at is purely a freshness/update-time signal — independent of which
// tender_date_time window is currently loaded, so this is a separate, table-wide query.
export const fetchLastCollectedAt = async () => {
  const { data, error } = await supabase
    .from('ebuysugar_tender_listings')
    .select('collected_at')
    .order('collected_at', { ascending: false })
    .limit(1);
  if (error) return { lastCollectedAt: null, error: error.message };
  return { lastCollectedAt: data?.[0]?.collected_at ?? null, error: null };
};

export const fetchMandiIndex = async () => {
  const mandiRes = await fetchMandiListings();
  if (mandiRes.error) return { mandiIndex: null, error: mandiRes.error };
  return { mandiIndex: buildMandiIndex(mandiRes.factories), error: null };
};

// Directory-wide lookup (normalizedFactoryName -> factory page slug), built from
// mi_mills — the master factory list — rather than from live Mandi inventory, so
// a factory name is clickable to its own factory page even when it has no current
// SugarHathi quota/offer. Never used to decide Buy Now eligibility; only fetchMandiIndex
// above (live mill_price_quotes) governs that.
export const fetchFactoryDirectoryIndex = async () => {
  const { data, error } = await supabase.from('mi_mills').select('id, canonical_name');
  if (error) return { directoryIndex: null, error: error.message };
  const index = new Map();
  (data || []).forEach((m) => {
    index.set(normalizeFactoryName(m.canonical_name), { millId: m.id, slug: slugify(m.canonical_name) });
  });
  return { directoryIndex: index, error: null };
};

// Fetches one date-range "block" of tenders (used for both the initial latest-7-days
// load and each subsequent Load More block), matched against the given Mandi index.
//
// Every tender_date_time is a distinct market data point — a factory re-listing the
// same grade on a later date is a NEW tender event, not a repeat of the earlier one.
// No factory+grade collapsing happens here: each row from the query is returned as
// its own record. Blocks use disjoint [start, end) date ranges, and a single SELECT
// cannot return the same row twice, so no row can ever appear more than once across
// the whole loaded set — there is nothing to de-duplicate.
export const fetchTenderBlock = async ({ startISO, endExclusiveISO, mandiIndex, directoryIndex }) => {
  const { data, error } = await supabase
    .from('ebuysugar_tender_listings')
    .select('id, tender_date_time, mill_name_raw, state_normalized, payment_date, lifting_date, grade_raw, season, rate_excl_gst')
    .gte('tender_date_time', startISO)
    .lt('tender_date_time', endExclusiveISO)
    .order('tender_date_time', { ascending: false });

  if (error) return { tenders: [], error: error.message };

  const tenders = (data || []).map((r) => {
    const mandiEntry = mandiIndex ? mandiIndex.get(normalizeFactoryName(r.mill_name_raw)) : null;
    const mandiGrade = mandiEntry ? mandiEntry.grades.get(normalizeGrade(r.grade_raw)) : null;
    const matched = !!(mandiGrade && Number(mandiGrade.availableQty) > 0);
    const directoryEntry = directoryIndex ? directoryIndex.get(normalizeFactoryName(r.mill_name_raw)) : null;
    return {
      id: r.id,
      tenderDateTime: r.tender_date_time,
      millName: r.mill_name_raw,
      state: r.state_normalized,
      grade: r.grade_raw,
      season: r.season,
      paymentDate: r.payment_date,
      liftingDate: r.lifting_date,
      rateExclGst: r.rate_excl_gst != null ? Number(r.rate_excl_gst) : null,
      matched,
      // Factory-page link — from the mi_mills master directory, independent of
      // whether SugarHathi currently has a matching Buy Now offer for this factory.
      directoryFactorySlug: directoryEntry ? directoryEntry.slug : null,
      mandiFactorySlug: matched ? mandiEntry.factorySlug : null,
      // The actual Mandi-side grade string + mill_price_quotes.id — passed through
      // to the Buy Now link so the destination page can identify the exact matching
      // offer (quote id first, grade string as fallback) rather than re-deriving it.
      mandiGradeCode: matched ? mandiGrade.grade : null,
      mandiQuoteId: matched ? mandiGrade.id : null,
    };
  });
  // Already ordered by tender_date_time desc via the query above.

  return { tenders, error: null };
};
